'use client';

import React from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { jsxMapLinkDefault, jsxMapLinkMobile } from '@/helpers/cinema-helper';
import { getOs, OS } from '@cineamo/ui/src/helpers/os-helper';

import { useClientValue } from '@/hooks/useClientValue';

/** Module scope so the reference is stable across renders — see {@link useClientValue}. */
const readOs = () => getOs();

type CinemaMapLinkProps = {
    cinema: CinemaDto;
    classNameLabel?: string;
    locale: string;
};

/**
 * The cinema's address as a link to the visitor's map app.
 *
 * The scheme (`maps:` / `geo:` on a phone, a plain map URL otherwise) depends on the OS, resolved in
 * the browser rather than from the `user-agent` header so the HTML does not differ per device. The
 * first render is the OS-independent link, which works everywhere; a phone gets its native scheme
 * one paint later.
 */
const CinemaMapLink: React.FC<CinemaMapLinkProps> = ({ cinema, classNameLabel, locale }) => {
    const os = useClientValue(readOs, OS.Other);

    const options = {
        cinema: cinema,
        style: { classNameLabel: classNameLabel },
        locale: locale
    };

    return os === OS.iOS || os === OS.Android ? jsxMapLinkMobile({ ...options, os: os }) : jsxMapLinkDefault(options);
};

export default CinemaMapLink;
